import { Clip } from '../../types/clip-types';
import { ClipDeletes, CompositeSegment } from '../../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { Word } from '../../types/transcript-types';

export const getFlattenedDeletes = ({ clipDeletes, fps }: { clipDeletes: ClipDeletes; fps: number }) => {
  if (clipDeletes === undefined) {
    return [];
  }
  return Object.values(clipDeletes)
    .map(data => ({
      fromFrame: data.bounds[0] * fps,
      toFrame: data.bounds[1] * fps
    }))
    .filter(data => data.toFrame !== data.fromFrame)
    .sort((a, b) => a.fromFrame - b.fromFrame);
}; // This is required to adjust the frame of the main segment to account for the deletes

export const getAdjustedFrame = (frame: number, deletes: ReturnType<typeof getFlattenedDeletes>, start: number) => {
  let adjustment = 0;

  for (const deleteData of deletes) {
    if (deleteData.fromFrame >= frame) {
      break;
    }
    if (deleteData.toFrame <= frame) {
      adjustment += deleteData.toFrame - deleteData.fromFrame;
    }
  }

  return frame - adjustment - start;
}; // This is required to get the available ranges after the deletes

export const getAvailableRanges = (
  totalRange: [number, number],
  deletedRanges: ReturnType<typeof getFlattenedDeletes>
) => {
  // Extract the start and end of the total range
  const [totalStart, totalEnd] = totalRange;

  // Sort the deleted ranges by their start value
  deletedRanges.sort((a, b) => a.fromFrame - b.fromFrame);

  const remainingRanges: [number, number][] = [];

  // Track the end of the last deleted range
  let lastEnd = totalStart;

  // Loop through the deleted ranges and find gaps
  for (let i = 0; i < deletedRanges.length; i++) {
    const { fromFrame, toFrame } = deletedRanges[i];

    // If there is a gap before the current deleted range
    if (fromFrame > lastEnd) {
      remainingRanges.push([lastEnd, fromFrame]);
    }

    // Update the last end point to the end of the current deleted range
    lastEnd = Math.max(lastEnd, toFrame);
  }

  // If there is any remaining range after the last deleted range
  if (lastEnd < totalEnd) {
    remainingRanges.push([lastEnd, totalEnd]);
  }

  return remainingRanges;
};
export function getSegmentTranslatedCaptions(newCompositeSegment: CompositeSegment, clip: Clip): Word[] {
  if (!newCompositeSegment.segment_metadata) {
    return [];
  }
  const segStart = newCompositeSegment.segment_metadata.start;
  const segEnd = newCompositeSegment.segment_metadata.end;
  const excludes = newCompositeSegment.segment_metadata.excludes || [];
  let captionsIndex = 0; // index setup is needed for selection since translated transcript is

  // pulled from segment_metadata.captions and not clip.asset_metadata.captions
  return (clip.asset_metadata.captions || [])
    .filter(
      caption =>
        typeof caption.start_time === 'number' &&
        typeof caption.end_time === 'number' &&
        caption.start_time >= segStart &&
        caption.end_time <= segEnd
    ) // get captions for segment time boundaries
    .filter(caption => {
      const wordEdit = newCompositeSegment.segment_metadata?.edits?.[caption.start_time];
      const excluded = excludes?.find(ex => caption.start_time >= ex[0] && caption.end_time <= ex[1]);
      return !excluded || !!wordEdit?.word;
    }) // hide deleted words
    .map(caption => {
      const wordEdit = newCompositeSegment.segment_metadata?.edits?.[caption.start_time];
      return {
        ...caption,
        processed_start_time: (caption.processed_start_time ?? caption.start_time) + segStart,
        processed_end_time: (caption.processed_end_time ?? caption.end_time) + segStart,
        content: wordEdit?.word ? wordEdit.word : caption.content,
        index: captionsIndex++
      };
    });
}
